function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;
    if (
      !this.sliderItemsToShow?.length ||
      !Number.isFinite(this.sliderItemOffset) ||
      this.sliderItemOffset <= 0
    )
      return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


/* bags-hero-hotfix-20260316 */
(function() {
  const target = "https://cdn.shopify.com/s/files/1/0698/6419/4361/files/bag-ban.png?v=1732653065";
  const run = () => {
    if (!window.location.pathname.match(/^\/collections\/bags$/)) return;
    const img =
      document.querySelector('div[id*="__d_hero_"] img[alt="Totes Adorable, Totes Functional"]') ||
      document.querySelector('img[alt="Totes Adorable, Totes Functional"]');
    if (!img) return;
    if (img.src !== target) {
      img.src = target;
      img.removeAttribute('srcset');
      img.setAttribute('data-bags-hero-hotfix', 'applied');
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  const observer = new MutationObserver(run);
  observer.observe(document.documentElement, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 5000);
})();


/* product-ugc-gallery-hotfix-20260318 */
(function() {
  const isProductPage = () => /^\/products\//.test(window.location.pathname);
  const isUgcHtml = (html) => {
    const lower = (html || '').toLowerCase();
    if (lower.indexOf('ugc_primary:') !== -1 || lower.indexOf('ugc_run:') !== -1) return true;
    return /_\d{2}_(top_left|center_large|product_native)\.(png|jpe?g)/.test(lower);
  };
  const reorderList = (list) => {
    if (!list || list.dataset.ugcGalleryHotfixApplied === '1') return;
    const items = Array.from(list.children);
    if (items.length < 3) return;
    const first = items[0];
    const rest = items.slice(1);
    const ugc = rest.filter((node) => isUgcHtml(node.innerHTML));
    if (!ugc.length) return;
    const nonUgc = rest.filter((node) => !isUgcHtml(node.innerHTML));
    [first, ...ugc, ...nonUgc].forEach((node) => list.appendChild(node));
    list.dataset.ugcGalleryHotfixApplied = '1';
  };
  const run = () => {
    if (!isProductPage()) return;
    document.querySelectorAll('[id^="Slider-Gallery-"], .product__media-list').forEach(reorderList);
    document.querySelectorAll('[id^="Slider-Thumbnails-"], .thumbnail-list').forEach(reorderList);
    document.body.setAttribute('data-product-ugc-gallery-hotfix', 'applied');
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  /* dd_pdp_gallery_hijack_fix_20260321 */
})();

/* targeted-product-ugc-media-swap-20260318 */
(function() {
  const swapsByPath = {
    '/products/kids-sweatshirt-custom-dog-face-line-art': [
      [
        '1007Janiewhite_fde6939f-fce1-4107-bd73-dec8a2768338',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/kids-sweatshirt-custom-dog-face-line-art_01_top_left.png?v=1773877697'
      ],
      [
        '1013tonyawhite_a8ee39ef-bdc7-4d54-b280-78c215257708',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/kids-sweatshirt-custom-dog-face-line-art_02_center_large_21c48a4f-2edb-4f70-85b6-054e734e147a.png?v=1773877688'
      ],
      [
        '1015Katherinewhite_42a9bf50-1a6a-414d-a565-5f734231722a',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/kids-sweatshirt-custom-dog-face-line-art_03_top_left.png?v=1773877689'
      ],
      [
        'wrinkledogwhite_591337d0-90a0-4500-affb-ac29c97b3e09',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/kids-sweatshirt-custom-dog-face-line-art_04_center_large_fdab1e60-593f-467d-9f78-15af70a51342.png?v=1773877690'
      ]
    ],
    '/products/toddlers-t-shirt-100-cotton-personalized-dog-shirt': [
      [
        '1007Janiewhite_eaddcb6b-d6cd-4d88-ae8f-78ec2c734059',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/toddlers-t-shirt-100-cotton-personalized-dog-shirt_01_top_left.png?v=1773877912'
      ],
      [
        '1013tonyawhite_849ae9fe-8e27-4525-afa3-af8a42e81700',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/toddlers-t-shirt-100-cotton-personalized-dog-shirt_02_center_large_bd5c66fc-f761-4fac-849b-c88d889c0242.png?v=1773877900'
      ],
      [
        '1015Katherinewhite_bf8f552a-ab4e-4036-9515-097f635d2561',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/toddlers-t-shirt-100-cotton-personalized-dog-shirt_03_top_left.png?v=1773877900'
      ],
      [
        'wrinkledogwhite_94b8fc95-a7d2-4440-b7bc-124d69264139',
        'https://cdn.shopify.com/s/files/1/0698/6419/4361/files/toddlers-t-shirt-100-cotton-personalized-dog-shirt_04_center_large_eb9239ef-c088-4685-be14-6f43e484b3fa.png?v=1773877901'
      ]
    ]
  };
  const swaps = swapsByPath[window.location.pathname];
  if (!swaps) return;

  const replaceOnElement = (el) => {
    if (!el) return;
    ['src', 'srcset', 'data-src'].forEach((attr) => {
      const current = el.getAttribute(attr);
      if (!current) return;
      let next = current;
      swaps.forEach(([from, to]) => {
        if (next.indexOf(from) !== -1) {
          next = attr === 'srcset' ? `${to}&width=110 110w, ${to}&width=550 550w, ${to} 1000w` : to;
        }
      });
      if (next !== current) el.setAttribute(attr, next);
    });
    if (el.tagName === 'IMG' && el.currentSrc) {
      swaps.forEach(([from, to]) => {
        if ((el.src || '').indexOf(from) !== -1) {
          el.src = to;
          el.removeAttribute('srcset');
        }
      });
    }
  };

  const run = () => {
    document.querySelectorAll('img, source').forEach(replaceOnElement);
    document.body.setAttribute('data-targeted-product-ugc-media-swap', 'applied');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  const observer = new MutationObserver(run);
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'data-src'] });
  setTimeout(() => observer.disconnect(), 12000);
})();

(() => {
  const ddHeaderAccountHotfix = () => {
    const svg = "<svg xmlns=\"http://www.w3.org/2000/svg\" aria-hidden=\"true\" focusable=\"false\" role=\"presentation\" class=\"icon icon-account\" fill=\"none\" viewBox=\"0 0 18 19\">\n  <path fill-rule=\"evenodd\" clip-rule=\"evenodd\" d=\"M6 4.5a3 3 0 116 0 3 3 0 01-6 0zm3-4a4 4 0 100 8 4 4 0 000-8zm5.58 12.15c1.12.82 1.83 2.24 1.91 4.85H1.51c.08-2.6.79-4.03 1.9-4.85C4.66 11.75 6.5 11.5 9 11.5s4.35.26 5.58 1.15zM9 10.5c-2.5 0-4.65.24-6.17 1.35C1.27 12.98.5 14.93.5 18v.5h17V18c0-3.07-.77-5.02-2.33-6.15-1.52-1.1-3.67-1.35-6.17-1.35z\" fill=\"currentColor\" />\n</svg>";
    const clean = () => {
      document.querySelectorAll('a.menu-drawer__account, a.header__icon--account').forEach((link) => {
        const text = (link.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text.includes('Could not find asset snippets/icon-account.liquid')) return;
        const label = /account/i.test(text) ? 'Account' : 'Log in';
        link.innerHTML = `${svg}${label}`;
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', clean, { once: true });
    } else {
      clean();
    }
    const observer = new MutationObserver(clean);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 12000);
  };
  ddHeaderAccountHotfix();
})();

/* Yotpo's generated filter labels omit their visible text sequence. */
(() => {
  if (!window.location.pathname.startsWith('/products/')) return;

  const improveReviewFilterNames = () => {
    const widgets = document.querySelectorAll('.yotpo-widget-instance');
    if (!widgets.length) return null;
    widgets.forEach((widget) => {
      const reviewRoot = widget.querySelector('.yotpo-reviews-main-widget');
      if (reviewRoot) {
        reviewRoot.style.setProperty('--primary-color', '#b23a2a', 'important');
        reviewRoot.style.setProperty('--text-color', '#211713', 'important');
        reviewRoot.style.setProperty('--background-color', '#fff', 'important');
      }
      widget.querySelectorAll('.yotpo-distribution-row__list-item button').forEach((button) => {
        const ratingElement = button.querySelector('.yotpo-star-distribution-index');
        const countElement = button.querySelector('.yotpo-star-distribution-value');
        const rating = ratingElement?.textContent.trim();
        const count = countElement?.textContent.trim();
        if (!rating || !count) return;
        if (!button.dataset.ddA11ySpacing) {
          countElement.before(document.createTextNode(' '));
          button.dataset.ddA11ySpacing = 'true';
        }
        const accessibleName = `${rating} ${count}. ${rating} stars with ${count} reviews. Apply this review rating filter.`;
        if (button.getAttribute('aria-label') !== accessibleName) {
          button.setAttribute('aria-label', accessibleName);
        }
      });
    });
    return widgets[0];
  };

  const start = () => {
    const observer = new MutationObserver(improveReviewFilterNames);
    observer.observe(document.documentElement, { childList: true, subtree: true });
    improveReviewFilterNames();
    const interval = setInterval(improveReviewFilterNames, 500);
    setTimeout(() => {
      observer.disconnect();
      clearInterval(interval);
    }, 30000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

(() => {
  if (!window.location.pathname.startsWith('/products/')) return;

  const startMobilePurchaseBar = () => {
    const bar = document.querySelector('[data-dd-mobile-purchase-bar]');
    const action = bar && bar.querySelector('[data-dd-mobile-purchase-action]');
    const productInfo = bar && bar.closest('product-info');
    if (!bar || !action || !productInfo) return;

    let chatPositionRetry = 0;
    let chatPositionAttempts = 0;
    const observedChatRoots = new WeakSet();
    let positionedChatLauncher = null;
    let chatLauncherDefaults = null;
    let chatLayoutFrame = 0;

    const restoreInlineProperty = (element, property, value, priority) => {
      if (
        element.style.getPropertyValue(property) === value &&
        element.style.getPropertyPriority(property) === priority
      ) {
        return;
      }
      if (value) {
        element.style.setProperty(property, value, priority);
      } else {
        element.style.removeProperty(property);
      }
    };

    const restoreChatLauncher = () => {
      if (!positionedChatLauncher || !chatLauncherDefaults) return;
      restoreInlineProperty(
        positionedChatLauncher,
        'bottom',
        chatLauncherDefaults.bottom,
        chatLauncherDefaults.bottomPriority
      );
      restoreInlineProperty(
        positionedChatLauncher,
        'visibility',
        chatLauncherDefaults.visibility,
        chatLauncherDefaults.visibilityPriority
      );
      restoreInlineProperty(
        positionedChatLauncher,
        'pointer-events',
        chatLauncherDefaults.pointerEvents,
        chatLauncherDefaults.pointerEventsPriority
      );
      delete positionedChatLauncher.dataset.ddPurchaseBarOffset;
      delete positionedChatLauncher.dataset.ddCollisionHidden;
    };

    const rememberChatLauncher = (launcher) => {
      if (positionedChatLauncher === launcher) return;
      restoreChatLauncher();
      positionedChatLauncher = launcher;
      chatLauncherDefaults = {
        bottom: launcher.style.getPropertyValue('bottom'),
        bottomPriority: launcher.style.getPropertyPriority('bottom'),
        computedBottom: Number.parseFloat(window.getComputedStyle(launcher).bottom) || 20,
        visibility: launcher.style.getPropertyValue('visibility'),
        visibilityPriority: launcher.style.getPropertyPriority('visibility'),
        pointerEvents: launcher.style.getPropertyValue('pointer-events'),
        pointerEventsPriority: launcher.style.getPropertyPriority('pointer-events')
      };
    };

    const visibleClickTargets = (chat) => {
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'summary',
        '[role="button"]',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      return Array.from(document.querySelectorAll(selector)).filter((element) => {
        if (element === chat || chat.contains(element)) return false;
        const style = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        return style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.pointerEvents !== 'none' &&
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top < window.innerHeight;
      });
    };

    const queueChatPosition = () => {
      if (chatLayoutFrame) return;
      chatLayoutFrame = window.requestAnimationFrame(() => {
        chatLayoutFrame = 0;
        syncChatPosition();
      });
    };

    const chatRootObserver = new MutationObserver(queueChatPosition);

    const observeChatRoots = (root) => {
      if (!root) return;
      if (!observedChatRoots.has(root)) {
        observedChatRoots.add(root);
        chatRootObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style']
        });
      }
      root.querySelectorAll('*').forEach((element) => {
        if (element.shadowRoot) observeChatRoots(element.shadowRoot);
      });
    };

    const findChatLauncher = (root) => {
      if (!root) return null;
      observeChatRoots(root);
      const directLauncher = root.querySelector('.activator-nudge');
      if (directLauncher) return directLauncher;
      for (const element of root.querySelectorAll('*')) {
        if (!element.shadowRoot) continue;
        const nestedLauncher = findChatLauncher(element.shadowRoot);
        if (nestedLauncher) return nestedLauncher;
      }
      const activator = root.querySelector('[part~="activator"][aria-label="Chat"]');
      return activator && (activator.closest('.activator-nudge') || activator.parentElement);
    };

    const syncChatPosition = () => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const chat = document.querySelector('shopify-chat');
      const chatRoot = chat && chat.shadowRoot;
      const launcher = findChatLauncher(chatRoot);
      if (!launcher) {
        if (isMobile && chatPositionAttempts < 40 && !chatPositionRetry) {
          chatPositionAttempts += 1;
          chatPositionRetry = window.setTimeout(() => {
            chatPositionRetry = 0;
            syncChatPosition();
          }, 250);
        }
        return;
      }

      chatPositionAttempts = 0;
      rememberChatLauncher(launcher);
      if (!isMobile) {
        restoreChatLauncher();
        return;
      }

      const launcherRect = launcher.getBoundingClientRect();
      const launcherHeight = launcherRect.height;
      const launcherLeft = launcherRect.left;
      const launcherRight = launcherRect.right;
      const stickyRect = !bar.hidden ? bar.getBoundingClientRect() : null;
      const minimumBottom = stickyRect && stickyRect.height > 0
        ? Math.ceil(stickyRect.height) + 12
        : chatLauncherDefaults.computedBottom;
      const obstacles = visibleClickTargets(chat).map((element) => element.getBoundingClientRect());
      if (stickyRect && stickyRect.height > 0) obstacles.push(stickyRect);

      const collidesAt = (top, obstacle) =>
        launcherLeft < obstacle.right + 8 &&
        launcherRight > obstacle.left - 8 &&
        top < obstacle.bottom + 8 &&
        top + launcherHeight > obstacle.top - 8;

      let candidateTop = window.innerHeight - minimumBottom - launcherHeight;
      for (let pass = 0; pass < 20 && candidateTop >= 12; pass += 1) {
        const collisions = obstacles.filter((obstacle) => collidesAt(candidateTop, obstacle));
        if (!collisions.length) break;
        candidateTop = Math.min(...collisions.map((obstacle) => obstacle.top)) - launcherHeight - 12;
      }

      const bottom = Math.round(window.innerHeight - candidateTop - launcherHeight);
      const maximumBottom = Math.max(
        minimumBottom,
        Math.min(360, Math.floor(window.innerHeight * 0.45))
      );
      if (
        candidateTop < 12 ||
        bottom > maximumBottom ||
        obstacles.some((obstacle) => collidesAt(candidateTop, obstacle))
      ) {
        restoreInlineProperty(launcher, 'visibility', 'hidden', 'important');
        restoreInlineProperty(launcher, 'pointer-events', 'none', 'important');
        launcher.dataset.ddCollisionHidden = 'true';
        return;
      }

      restoreInlineProperty(
        launcher,
        'visibility',
        chatLauncherDefaults.visibility,
        chatLauncherDefaults.visibilityPriority
      );
      restoreInlineProperty(
        launcher,
        'pointer-events',
        chatLauncherDefaults.pointerEvents,
        chatLauncherDefaults.pointerEventsPriority
      );
      delete launcher.dataset.ddCollisionHidden;
      restoreInlineProperty(launcher, 'bottom', `${bottom}px`, 'important');
      launcher.dataset.ddPurchaseBarOffset = 'true';
    };

    const productLayoutObserver = new MutationObserver(queueChatPosition);
    productLayoutObserver.observe(productInfo, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'hidden', 'open', 'style']
    });

    const visibleAddButton = () => Array.from(
      productInfo.querySelectorAll('.gpo-clone-atc-button, button[name="add"]')
    ).find((button) => {
      const style = window.getComputedStyle(button);
      const rect = button.getBoundingClientRect();
      return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
    });

    const setVisible = (visible) => {
      if (bar.hidden !== !visible) {
        bar.hidden = !visible;
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        document.body.classList.toggle('dd-mobile-purchase-bar-visible', visible);
      }
      syncChatPosition();
    };

    let frame = 0;
    const update = () => {
      frame = 0;
      if (!window.matchMedia('(max-width: 749px)').matches) {
        setVisible(false);
        return;
      }
      const addButton = visibleAddButton();
      setVisible(Boolean(addButton && addButton.getBoundingClientRect().bottom < 0));
    };
    const queueUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    action.addEventListener('click', () => {
      const standardPhoto = productInfo.querySelector(
        'input.gpo-file[name="Pet-image"]:not(:disabled), input#file-1[name="Pet-image"]:not(:disabled), input[data-dd-native-photo]:not(:disabled)'
      );
      const target = (standardPhoto && (standardPhoto.closest('.gpo-element') || standardPhoto)) ||
        productInfo.querySelector('variant-selects') ||
        visibleAddButton();
      if (target && target.scrollIntoView) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'dd_mobile_purchase_return',
        product_handle: productInfo.dataset.url ? productInfo.dataset.url.split('/products/').pop().split('?')[0] : ''
      });
    });

    window.addEventListener('scroll', queueUpdate, { passive: true });
    window.addEventListener('resize', queueUpdate, { passive: true });
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startMobilePurchaseBar, { once: true });
  } else {
    startMobilePurchaseBar();
  }
})();

(() => {
  if (!window.location.pathname.startsWith('/products/')) return;

  const startNativePhotoGuard = () => {
    const input = document.querySelector('input[data-dd-native-photo]');
    const field = input && input.closest('[data-dd-native-photo-field]');
    const form = input && input.form;
    const error = field && field.querySelector('[data-dd-native-photo-error]');
    if (!input || !field || !form || !error) return;

    const clearError = () => {
      field.classList.remove('has-error');
      error.textContent = '';
    };
    const guard = (event) => {
      if (input.disabled || (input.files && input.files.length)) {
        clearError();
        return true;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      field.classList.add('has-error');
      error.textContent = 'Please add your pet photo before adding this hat to the cart.';
      field.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      input.focus({ preventScroll: true });
      return false;
    };

    input.addEventListener('change', clearError);
    form.addEventListener('submit', guard, { capture: true });
    document.addEventListener('click', (event) => {
      const button = event.target && event.target.closest &&
        event.target.closest('button[name="add"], input[type="submit"][name="add"]');
      if (!button || (button.form !== form && !form.contains(button))) return;
      guard(event);
    }, { capture: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startNativePhotoGuard, { once: true });
  } else {
    startNativePhotoGuard();
  }
})();

(() => {
  if (window.ddPreAdsTrackingBridgeV7) return;
  window.ddPreAdsTrackingBridgeV7 = true;

  const STORAGE_PREFIX = 'dd_pre_ads';
  const GA4_MEASUREMENT_ID = 'G-WN0SELMF2M';
  const META_PIXEL_ID = '1387302608699240';
  const ADD_SELECTOR = 'button.gpo-clone-atc-button';
  const CHECKOUT_SELECTOR = "a.gpo-clone-checkout-button, button.gpo-clone-checkout-button, button[name='checkout'], input[name='checkout'], #checkout";

  const now = () => Date.now();
  const safeParse = (value) => {
    try {
      return JSON.parse(value || 'null');
    } catch (error) {
      return null;
    }
  };
  const sessionGet = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };
  const sessionSet = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      return null;
    }
  };
  const sessionRemove = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      return null;
    }
  };
  const money = (cents) => Number((Number(cents || 0) / 100).toFixed(2));
  const currency = () => window.Shopify?.currency?.active || 'USD';

  const readGaClientId = () => {
    const match = document.cookie.match(/(?:^|; )_ga=GA\d+\.\d+\.(\d+\.\d+)/);
    return match && match[1] ? match[1] : `${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
  };

  const readGaSessionId = () => {
    const match = document.cookie.match(/(?:^|; )_ga_[^=]+=GS\d+\.\d+\.(\d+)\./);
    return match && match[1] ? match[1] : String(Math.floor(Date.now() / 1000));
  };

  const fireImage = (url) => {
    const img = new Image();
    img.src = url;
  };

  const analyticsProcessingAllowed = () => {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !privacy ||
        typeof privacy.analyticsProcessingAllowed !== 'function' ||
        privacy.analyticsProcessingAllowed();
    } catch (error) {
      return false;
    }
  };

  const emitGa = (name, payload) => {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, Object.assign({ send_to: GA4_MEASUREMENT_ID }, payload));
      }
    } catch (error) {
      console.warn('dd pre-ads gtag emit failed', error);
    }

    const params = new URLSearchParams();
    params.set('v', '2');
    params.set('tid', GA4_MEASUREMENT_ID);
    params.set('cid', readGaClientId());
    params.set('sid', readGaSessionId());
    params.set('en', name);
    params.set('dl', location.href);
    params.set('dt', document.title);
    params.set('ul', (navigator.language || 'en-us').toLowerCase());
    params.set('sr', `${window.innerWidth || screen.width}x${window.innerHeight || screen.height}`);
    params.set('cu', payload.currency || currency());
    params.set('epn.value', String(payload.value || 0));
    (payload.items || []).slice(0, 5).forEach((item, index) => {
      const parts = [
        `id${item.item_id || ''}`,
        `nm${item.item_name || ''}`,
        `br${item.item_brand || 'Dilly Dally Dog Co'}`,
        `ca${item.item_category || 'custom pet apparel'}`,
        `pr${item.price || 0}`,
        `qt${item.quantity || 1}`,
      ];
      if (item.item_variant) parts.push(`va${item.item_variant}`);
      params.set(`pr${index + 1}`, parts.join('~'));
    });
    fireImage(`https://www.google-analytics.com/g/collect?${params.toString()}`);
  };

  const emitMeta = (name, payload, options = {}) => {
    if (!analyticsProcessingAllowed()) return false;
    let emittedWithFbq = false;
    try {
      if (typeof window.fbq === 'function') {
        const method = options.custom ? 'trackCustom' : 'track';
        const eventOptions = options.eventId ? { eventID: options.eventId } : undefined;
        window.fbq(method, name, payload, eventOptions);
        emittedWithFbq = true;
      }
    } catch (error) {
      console.warn('dd pre-ads fbq emit failed', error);
    }

    // The direct request is only a fallback. Sending it after fbq duplicates events.
    if (emittedWithFbq) return true;

    const params = new URLSearchParams();
    params.set('id', META_PIXEL_ID);
    params.set('ev', name);
    params.set('dl', location.href);
    params.set('rl', document.referrer || '');
    params.set('if', 'false');
    params.set('ts', String(Date.now()));
    params.set('sw', String(screen.width || window.innerWidth || 0));
    params.set('sh', String(screen.height || window.innerHeight || 0));
    params.set('cd[value]', String(payload.value || 0));
    params.set('cd[currency]', payload.currency || currency());
    params.set('cd[content_type]', payload.content_type || 'product');
    if (payload.content_ids) params.set('cd[content_ids]', JSON.stringify(payload.content_ids));
    if (payload.content_name) params.set('cd[content_name]', payload.content_name);
    if (payload.content_category) params.set('cd[content_category]', payload.content_category);
    if (payload.lead_type) params.set('cd[lead_type]', payload.lead_type);
    if (options.eventId) params.set('eid', options.eventId);
    fireImage(`https://www.facebook.com/tr?${params.toString()}`);
    return true;
  };

  const emitPreviewMetaOnce = (eventName, metaName, custom) => {
    document.addEventListener(eventName, (event) => {
      const detail = event && event.detail ? event.detail : {};
      const identity = detail.job_id || `${detail.product_handle || 'unknown'}:${detail.variant_id || 'unknown'}`;
      const firedKey = `${STORAGE_PREFIX}:preview:${metaName}:${identity}`;
      if (sessionGet(firedKey) === '1') return;
      const emitted = emitMeta(metaName, {
        content_type: 'product',
        content_ids: detail.variant_id ? [String(detail.variant_id)] : [],
        content_name: detail.product_handle || 'custom-pet-art-preview',
        content_category: 'custom pet apparel',
        lead_type: custom ? 'ai_preview_generated' : 'ai_preview_email',
        value: 0,
        currency: currency(),
      }, {
        custom,
        eventId: `dd-preview-${metaName}-${identity}`,
      });
      if (emitted) sessionSet(firedKey, '1');
    });
  };

  emitPreviewMetaOnce('dd_ai_preview_generation_completed', 'AIPreviewGenerated', true);
  emitPreviewMetaOnce('dd_ai_preview_email_submitted', 'Lead', false);

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return response.json();
  };

  const itemFromDom = () => {
    const form = document.querySelector('product-form form[action*="/cart/add"], form[action*="/cart/add"]');
    if (!form) return null;
    const variantId = form.querySelector('input[name="id"]')?.value;
    if (!variantId) return null;
    const title = (document.querySelector('.product__title h1, h1.product__title')?.textContent || document.title || '').trim();
    const itemVariant = Array.from(document.querySelectorAll('fieldset input[type="radio"]:checked'))
      .map((input) => input.value)
      .join(' / ');
    const numericPrice = ((document.querySelector('.price__sale .price-item--sale, .price__regular .price-item--regular, .price .price-item')?.textContent) || '')
      .replace(/[^0-9.]/g, '');
    const price = numericPrice ? Number(numericPrice) : 0;
    const quantity = Number(form.querySelector('input[name="quantity"]')?.value || 1) || 1;
    return {
      item_id: String(variantId),
      item_name: title,
      item_brand: 'Dilly Dally Dog Co',
      item_category: 'custom pet apparel',
      item_variant: itemVariant,
      price,
      quantity,
      currency: currency(),
      product_path: location.pathname,
      captured_at: now(),
    };
  };

  const cartLineToItem = (line) => {
    const quantity = Number(line.quantity || 1) || 1;
    const price = money(line.final_price ?? line.price ?? 0);
    return {
      item_id: String(line.variant_id || line.id || ''),
      item_name: line.product_title || line.title || '',
      item_brand: line.vendor || 'Dilly Dally Dog Co',
      item_category: 'custom pet apparel',
      item_variant: line.variant_title || '',
      price,
      quantity,
    };
  };

  const isAuxiliaryLine = (line) => {
    const handle = String(line.handle || '');
    const props = line && line.properties ? line.properties : {};
    return handle.startsWith('option-set-') || Boolean(props._gpo_parent_product_group);
  };

  const emitAddToCartFromPending = async () => {
    const pending = safeParse(sessionGet(`${STORAGE_PREFIX}:pending_add`));
    if (!pending || now() - Number(pending.captured_at || 0) > 10 * 60 * 1000) return;
    if (location.pathname !== '/cart') return;
    const firedKey = `${STORAGE_PREFIX}:add_fired:${pending.item_id}:${pending.captured_at}`;
    if (sessionGet(firedKey) === '1') return;

    const cart = await fetchCart();
    const matching = (cart.items || [])
      .filter((line) => !isAuxiliaryLine(line))
      .filter((line) => String(line.variant_id || line.id || '') === String(pending.item_id));

    const items = (matching.length ? matching : (cart.items || []).filter((line) => !isAuxiliaryLine(line)).slice(0, 1))
      .map(cartLineToItem);
    if (!items.length) return;

    const value = Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2));
    const payload = { currency: cart.currency || pending.currency || currency(), value, items };
    emitGa('add_to_cart', payload);
    emitMeta('AddToCart', {
      content_type: 'product',
      content_ids: items.map((item) => item.item_id),
      contents: items.map((item) => ({ id: item.item_id, quantity: item.quantity, item_price: item.price })),
      value,
      currency: payload.currency,
    });
    sessionSet(firedKey, '1');
    sessionRemove(`${STORAGE_PREFIX}:pending_add`);
  };

  const emitBeginCheckout = async () => {
    const cart = await fetchCart();
    const items = (cart.items || []).filter((line) => !isAuxiliaryLine(line)).map(cartLineToItem);
    if (!items.length) return;
    const value = Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2));
    const payload = { currency: cart.currency || currency(), value, items };
    emitGa('begin_checkout', payload);
    emitMeta('InitiateCheckout', {
      content_type: 'product',
      content_ids: items.map((item) => item.item_id),
      contents: items.map((item) => ({ id: item.item_id, quantity: item.quantity, item_price: item.price })),
      num_items: items.reduce((sum, item) => sum + item.quantity, 0),
      value,
      currency: payload.currency,
    });
  };

  document.addEventListener('click', (event) => {
    const addTarget = event.target instanceof Element ? event.target.closest(ADD_SELECTOR) : null;
    if (addTarget) {
      const item = itemFromDom();
      if (item) sessionSet(`${STORAGE_PREFIX}:pending_add`, JSON.stringify(item));
      return;
    }

    const checkoutTarget = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
    if (!checkoutTarget || location.pathname !== '/cart') return;
    if (checkoutTarget.dataset.ddPreAdsPassthrough === '1') {
      delete checkoutTarget.dataset.ddPreAdsPassthrough;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    emitBeginCheckout()
      .catch((error) => console.warn('dd pre-ads begin_checkout failed', error))
      .finally(() => {
        checkoutTarget.dataset.ddPreAdsPassthrough = '1';
        setTimeout(() => checkoutTarget.click(), 180);
      });
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      emitAddToCartFromPending().catch((error) => console.warn('dd pre-ads add_to_cart failed', error));
    }, { once: true });
  } else {
    emitAddToCartFromPending().catch((error) => console.warn('dd pre-ads add_to_cart failed', error));
  }
})();

/* dd_pdp_runtime_overflow_clamp_20260322 */
(() => {
  const run = () => {
    if (!/^\/(?:[a-z]{2}\/)?products\//.test(window.location.pathname)) return;
    document.documentElement.style.overflowX = 'clip';
    document.body.style.overflowX = 'clip';
    document.querySelectorAll('#MainContent, body.gradient product-info, body.gradient .page-width').forEach((el) => {
      el.style.overflowX = 'clip';
      el.style.maxWidth = '100%';
    });
    const product = document.querySelector('body.gradient .product');
    if (product) {
      product.style.position = 'relative';
      product.style.overflow = 'hidden';
      product.style.maxWidth = '100%';
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
  setTimeout(run, 600);
  setTimeout(run, 1800);
})();

/* Keep third-party add-to-cart clones from duplicating the theme button ID. */
(() => {
  const normalizeProductSubmitIds = () => {
    document.querySelectorAll('.gpo-clone-atc-button[id]').forEach((clone) => {
      const canonicalId = (clone.dataset.ddOriginalButtonId || clone.id).replace(/--gpo-original$/, '');
      if (clone.id !== canonicalId) clone.id = canonicalId;
      delete clone.dataset.ddOriginalButtonId;
    });

    document.querySelectorAll('.gpo-original-atc-button[id]').forEach((original) => {
      const canonicalId = (original.dataset.ddOriginalButtonId || original.id).replace(/--gpo-original$/, '');
      const clone = Array.from(document.querySelectorAll('.gpo-clone-atc-button[id]')).find(
        (element) => element.id === canonicalId,
      );
      if (!clone) return;
      original.dataset.ddOriginalButtonId = canonicalId;
      const originalId = `${canonicalId}--gpo-original`;
      if (original.id !== originalId) original.id = originalId;
    });
  };

  const normalizeReviewLabelIds = () => {
    const seen = new Map();
    document.querySelectorAll('.yotpo-review-votes-text[id]').forEach((label) => {
      const originalId = label.id.replace(/--dd-\d+$/, '');
      const occurrence = seen.get(originalId) || 0;
      seen.set(originalId, occurrence + 1);
      if (occurrence === 0) return;
      const uniqueId = `${originalId}--dd-${occurrence + 1}`;
      if (label.id !== uniqueId) label.id = uniqueId;
      const wrapper = label.closest('.yotpo-review-votes-wrapper');
      if (wrapper) wrapper.setAttribute('aria-labelledby', uniqueId);
    });
  };

  const normalizeInjectedMarkup = () => {
    normalizeProductSubmitIds();
    normalizeReviewLabelIds();
  };

  const start = () => {
    normalizeInjectedMarkup();
    const observer = new MutationObserver(normalizeInjectedMarkup);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'id'] });
    setTimeout(normalizeInjectedMarkup, 600);
    setTimeout(normalizeInjectedMarkup, 1800);
    setTimeout(normalizeInjectedMarkup, 3200);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
